/**
 * RaiseDispute.jsx — Raise a dispute for an active deal.
 *
 * Fields: dispute reason (text area), evidence files (up to 3, ≤20 MB each).
 * Handles: EVIDENCE_FILE_LIMIT_EXCEEDED, FILE_TOO_LARGE.
 *
 * Requirements: 23.7
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';

const MAX_FILES = 3;
const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ERROR_MESSAGES = {
  EVIDENCE_FILE_LIMIT_EXCEEDED: `Maximum ${MAX_FILES} evidence files allowed.`,
  FILE_TOO_LARGE: `Each evidence file must be ≤${MAX_FILE_SIZE_MB} MB.`,
  INVALID_MIME_TYPE: 'Invalid file type. Accepted: PDF, JPG, PNG, DOCX.',
  DEAL_STATUS_INVALID: 'Disputes can only be raised on active or in-progress deals.',
};

export default function RaiseDispute() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = React.useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return [sp];
  }, []);
  const milestoneId = new URLSearchParams(window.location.search).get('milestone_id');

  const [disputeReason, setDisputeReason] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [legalTriggerReasons, setLegalTriggerReasons] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    const combined = [...evidenceFiles, ...selected].slice(0, MAX_FILES);

    // Client-side size check
    const oversized = combined.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized) {
      setErrors((prev) => ({
        ...prev,
        files: `"${oversized.name}" exceeds the ${MAX_FILE_SIZE_MB} MB limit.`,
      }));
      return;
    }

    setEvidenceFiles(combined);
    setErrors((prev) => ({ ...prev, files: '' }));
    setApiError('');
    // Reset the input so the same file can be re-selected after removal
    e.target.value = '';
  };

  const removeFile = (index) => {
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs = {};
    if (!disputeReason.trim() || disputeReason.trim().length < 10)
      errs.disputeReason = 'Please provide a detailed reason (at least 10 characters).';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setApiError('');
    setLegalTriggerReasons([]);

    const formData = new FormData();
    formData.append('deal_id', dealId);
    formData.append('dispute_reason', disputeReason);
    if (milestoneId) formData.append('milestone_id', milestoneId);
    evidenceFiles.forEach((f) => formData.append('evidence_files', f));

    try {
      const { data } = await apiClient.post('/api/v1/disputes/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (Array.isArray(data?.legal_trigger_reasons)) {
        setLegalTriggerReasons(data.legal_trigger_reasons);
      }
      setSubmitted(true);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setApiError(ERROR_MESSAGES[code] || 'Failed to raise dispute. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Dispute Raised
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Your dispute has been submitted. An admin will review it and contact you.
          {evidenceFiles.length > 0 && ` ${evidenceFiles.length} evidence file(s) uploaded.`}
        </Typography>
        {legalTriggerReasons.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
            Legal escalation may be required for this dispute.
            <br />
            Triggers: {legalTriggerReasons.join(', ')}
          </Alert>
        )}
        <Button variant="outlined" onClick={() => navigate(`/deals/${dealId}`)}>
          Back to Deal
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 560, mx: 'auto', mt: 4, p: 3 }}
      noValidate
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/deals/${dealId}`)}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to Deal
      </Button>
      <Typography variant="h4" gutterBottom>
        Raise a Dispute
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Deal #{dealId}{milestoneId ? ` — Milestone #${milestoneId}` : ''}
      </Typography>

      <TextField
        label="Dispute Reason *"
        value={disputeReason}
        onChange={(e) => {
          setDisputeReason(e.target.value);
          setErrors((prev) => ({ ...prev, disputeReason: '' }));
          setApiError('');
        }}
        error={!!errors.disputeReason}
        helperText={errors.disputeReason}
        fullWidth
        required
        multiline
        rows={5}
        margin="normal"
        inputProps={{ 'aria-label': 'Dispute reason' }}
      />

      {/* Evidence file upload */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Evidence Files (optional, up to {MAX_FILES} files, ≤{MAX_FILE_SIZE_MB} MB each)
        </Typography>

        {evidenceFiles.length < MAX_FILES && (
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFileIcon />}
            aria-label="Attach evidence file"
          >
            Attach File
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={handleFileChange}
              aria-label="Evidence file"
            />
          </Button>
        )}

        {errors.files && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {errors.files}
          </Typography>
        )}

        {evidenceFiles.length > 0 && (
          <List dense sx={{ mt: 1 }}>
            {evidenceFiles.map((f, i) => (
              <ListItem
                key={i}
                secondaryAction={
                  <Chip
                    label="Remove"
                    size="small"
                    onClick={() => removeFile(i)}
                    onDelete={() => removeFile(i)}
                    aria-label={`Remove ${f.name}`}
                  />
                }
              >
                <ListItemText
                  primary={f.name}
                  secondary={`${(f.size / 1024 / 1024).toFixed(2)} MB`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="error"
        fullWidth
        disabled={loading}
        sx={{ mt: 3 }}
        aria-label="Submit dispute"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Raise Dispute'}
      </Button>
      {apiError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiError}
        </Alert>
      )}
    </Box>
  );
}
